import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Container,
    Paper,
    Alert
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [rateLimitError, setRateLimitError] = useState('');
    const [generalError, setGeneralError] = useState('');

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
        // Clear email error when user starts typing
        if (emailError) setEmailError('');
        if (generalError) setGeneralError('');
        if (rateLimitError) setRateLimitError('');
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSendEmail = async () => {
        // Clear previous errors
        setEmailError('');
        setGeneralError('');
        setRateLimitError('');

        // Client-side validation
        if (!email) {
            setEmailError('This field is required.');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        
        try {
            await AuthService.requestPasswordReset(email);
            
            // On success (200), show success state
            setRequestSent(true);
        } catch (error) {
            console.error('Error sending password reset email:', error);
            
            // Handle different error types
            if (error.status === 400) {
                // Validation errors from backend
                if (error.data && error.data.email) {
                    // Join error messages if multiple
                    const emailErrors = Array.isArray(error.data.email) 
                        ? error.data.email.join(' ') 
                        : error.data.email;
                    setEmailError(emailErrors);
                } else if (error.data && typeof error.data === 'object') {
                    // Generic validation error
                    const firstKey = Object.keys(error.data)[0];
                    const firstError = error.data[firstKey];
                    setEmailError(Array.isArray(firstError) ? firstError.join(' ') : firstError);
                } else {
                    setGeneralError(getSafeApiErrorMessage(error, 'Invalid request. Please check your input.'));
                }
            } else if (error.status === 429) {
                // Rate limiting
                const retryAfter = error.data?.retry_after || error.data?.retryAfter;
                if (retryAfter) {
                    setRateLimitError(`Too many attempts. Please wait ${retryAfter} seconds and try again.`);
                } else {
                    setRateLimitError('Too many attempts. Please wait a moment and try again.');
                }
            } else if (error.status >= 500) {
                // Server error
                setGeneralError('Server error. Please try again later.');
            } else if (isNetworkError(error)) {
                // Network error
                setGeneralError('Server unreachable. Is the backend running?');
            } else {
                // Unexpected error
                setGeneralError(getSafeApiErrorMessage(error, 'Unexpected server response.'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            backgroundColor: '#f7f9fb',
            fontFamily: 'inherit'
        }}>
            <Container maxWidth="lg">
                <Paper 
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: '20px',
                        overflow: 'hidden',
                        minHeight: '600px',
                        boxShadow: '0 18px 36px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    {/* Left Side - Form */}
                    <Box sx={{
                        flex: 1,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'background.paper',
                        position: 'relative'
                    }}>
                        <Box sx={{ width: '100%', maxWidth: '400px' }}>
                            {/* Back Button */}
                            <Button
                                onClick={handleBackToLogin}
                                sx={{
                                    mb: 2,
                                    color: '#2D5EFF',
                                    '&:hover': {
                                        backgroundColor: 'rgba(45, 94, 255, 0.08)'
                                    }
                                }}
                            >
                                ← Back to sign in
                            </Button>

                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                                <img
                                    src="/logo.png"
                                    alt="DataFre8"
                                    style={{ height: '20px', width: 'auto' }}
                                />
                            </Box>
                            
                            {requestSent ? (
                                // Success State
                                <>
                                    <Typography variant="h4" fontWeight={600} textAlign="center" sx={{ mt: 0, mb: 1 }}>
                                        Check your email
                                    </Typography>
                                    
                                    <Alert severity="success" sx={{ mb: 3 }}>
                                        If that email exists in our system, we've sent a password reset link.
                                        Please check your inbox (and spam folder).
                                    </Alert>
                                    
                                    <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 3, fontSize: '0.9rem' }}>
                                        Didn't receive an email? Check your spam folder or try again with a different email address.
                                    </Typography>
                                    
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleBackToLogin}
                                        sx={{
                                            py: 1.5,
                                            backgroundColor: '#3B82F6',
                                            '&:hover': {
                                                backgroundColor: '#3B82F6',
                                                filter: 'brightness(0.95)'
                                            }
                                        }}
                                    >
                                        Back to Sign in
                                    </Button>
                                </>
                            ) : (
                                // Request Form
                                <>
                                    <Typography variant="h4" fontWeight={600} textAlign="center" sx={{ mt: 0, mb: 1 }}>
                                        Forgot your password?
                                    </Typography>
                                    
                                    <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
                                        Enter the email associated with your account and we'll send you a link to reset your password.
                                    </Typography>

                                    {/* General Error Alert */}
                                    {generalError && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {generalError}
                                        </Alert>
                                    )}

                                    {/* Rate Limit Error Alert */}
                                    {rateLimitError && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            {rateLimitError}
                                        </Alert>
                                    )}

                                    {/* Email Field */}
                                    <TextField
                                        type="email"
                                        label="Email Address"
                                        placeholder="your@email.com"
                                        fullWidth
                                        value={email}
                                        onChange={handleEmailChange}
                                        error={!!emailError}
                                        helperText={emailError}
                                        sx={{ mb: 3 }}
                                    />

                                    {/* Send Reset Link Button */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={handleSendEmail}
                                        disabled={isSubmitting || !!rateLimitError}
                                        sx={{
                                            py: 1.5,
                                            backgroundColor: '#3B82F6',
                                            '&:hover': {
                                                backgroundColor: '#3B82F6',
                                                filter: 'brightness(0.95)'
                                            },
                                            '&:disabled': {
                                                backgroundColor: '#ccc'
                                            }
                                        }}
                                    >
                                        {isSubmitting ? 'Sending...' : 'SEND RESET LINK'}
                                    </Button>

                                    {/* Back to Login Link */}
                                    <Typography 
                                        variant="body2" 
                                        textAlign="center" 
                                        sx={{ 
                                            mt: 3, 
                                            cursor: 'pointer',
                                            color: '#2D5EFF',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                        onClick={handleBackToLogin}
                                    >
                                        Back to Sign in
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Right Side - Background Image */}
                    <Box sx={{
                        flex: 1,
                        backgroundImage: `url(/lantern.png)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'saturate(0.9) contrast(0.95)',
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        textAlign: 'center',
                        color: '#fff',
                        p: 3,
                        position: 'relative'
                    }}>
                        {/* Background overlay to soften contrast */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255, 255, 255, 0.12)',
                            zIndex: 1
                        }} />
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPassword; 
